import React, { useContext, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import { useItemSettings } from '@hooks/useItemSettings'
import { uploadImage } from '@helpers/api'
import { storagePath } from '@helpers/variables'
import Button from '@components/Button'
import Heading from '@components/Heading'
import GridColumns from '@components/GridColumns'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Loader from '@components/Loader'
import Text from '@components/Text'
import { useUpdateSection } from '@hooks/mutations/useUpdateSection'
import ButtonWithLoader from '@components/ButtonWithLoader'

import { mutate } from 'swr'
import { Single_Book } from '@graphql/queries'

const EditSectionPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { currentSection, currentEditBookObject },
  } = state

  const {
    schoolYears,
    subjectOptions,
    levelOptions,
    tags,
    isLoading: itemSettingsLoading,
    error: itemSettingsError,
  } = useItemSettings()

  const { post, isPosting } = useUpdateSection()

  const { register, handleSubmit, errors, reset, control, formState } = useForm({
    defaultValues: {
      name: currentSection.name,
      description: currentSection.description,
      subject: currentSection.subject.id,
      schoolYear: currentSection.schoolYear.id,
      level: currentSection.level.id,
      tags: currentSection.tags,
    },
  })
  const { isDirty } = formState

  const onSubmit = async (data) => {
    let fileName = ''

    if (data.image.length > 0) {
      // User has selected new image, upload it!
      try {
        fileName = await uploadImage(data.image[0])
      } catch (error) {
        console.log(error)
        // show toast here?
      }
    } else if (currentSection.image) {
      fileName = currentSection.image
    }

    const tagIdsToConnect = data.tags.reduce((acc, tag) => {
      if (tag.hasOwnProperty('id')) {
        const alreadyPresent = currentSection.tags.find(
          (existingTag) => existingTag.id === tag.id
        )

        if (!alreadyPresent) {
          return [...acc, tag.id]
        } else {
          return acc
        }
      } else {
        return acc
      }
    }, [])

    const tagIdsToDisconnect = currentSection.tags.reduce(
      (acc, previouslyExistingTag) => {
        const stillPresent = data.tags.find(
          (tag) => tag.id === previouslyExistingTag.id
        )

        if (!stillPresent) {
          return [...acc, previouslyExistingTag.id]
        } else {
          return acc
        }
      },
      []
    )

    const tagsToCreate = data.tags.reduce((acc, tag) => {
      if (!tag.hasOwnProperty('id')) {
        return [...acc, { name: tag.name }]
      } else {
        return acc
      }
    }, [])

    const res = await post({
      id: currentSection.id,
      name: data.name,
      description: data.description,
      subject: data.subject,
      schoolYear: data.schoolYear,
      level: data.level,
      connectTags: tagIdsToConnect,
      disconnectTags: tagIdsToDisconnect,
      createTags: tagsToCreate,
      image: fileName,
    })

    if (res.success) {
      dispatch(closePanel(PANELS.EDIT_SECTION))

      // If we are in editing book view, refetch book
      if (currentEditBookObject) {
        await mutate([Single_Book, currentEditBookObject.id]) // refetch current book
      }
    }
  }

  return (
    <>
      <div className='flex justify-between items-center'>
        <Text>Redigera avsnitt</Text>
        <Button
          handleClick={() => {
            dispatch(closePanel(PANELS.EDIT_SECTION))
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        ></Button>
      </div>

      <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
        Om mitt avsnitt
      </Heading>

      <Divider size={DIVIDER_SIZE.MEDIUM} />

      {itemSettingsLoading && <Loader />}
      {itemSettingsError && <p>Error loading item settings</p>}

      {!itemSettingsLoading && !itemSettingsError && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Label>Välj årskurs*</Form.Label>
          {schoolYears.map((schoolYear, index) => {
            const { id, name } = schoolYear
            return (
              <Form.Radio
                key={`schoolYear${index}`}
                name={'schoolYear'}
                id={name}
                ref={register({ required: true })}
                label={`Årskurs ${name}`}
                value={id}
              />
            )
          })}
          {errors.schoolYear && (
            <Form.ErrorMessage>Årskurs behövs</Form.ErrorMessage>
          )}

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Form.Label htmlFor='image'>Välj en bild</Form.Label>
          <Form.ImageUpload
            name='image'
            id='image'
            ref={register()}
            imagePreviewUrl={
              currentSection.image
                ? `${storagePath}/${currentSection.image}`
                : null
            }
          />

          <Divider size={DIVIDER_SIZE.SMALL} />

          <GridColumns cols={2}>
            <div>
              <Form.Label htmlFor='subject'>Ämne*</Form.Label>
              <Form.Select
                name='subject'
                id='subject'
                ref={register({ required: true })}
                options={[{ value: '', name: 'Välj...' }, ...subjectOptions]}
              />
              {errors.subject && (
                <Form.ErrorMessage>Ämne behövs</Form.ErrorMessage>
              )}
            </div>
            <div>
              <Form.Label htmlFor='level'>Nivå*</Form.Label>
              <Form.Select
                name='level'
                id='level'
                ref={register({ required: true })}
                options={[{ value: '', name: 'Välj...' }, ...levelOptions]}
              />
              {errors.level && (
                <Form.ErrorMessage>Nivå behövs</Form.ErrorMessage>
              )}
            </div>
          </GridColumns>

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Form.Label htmlFor='name'>Namnge avsnittet*</Form.Label>
          <Form.Input
            type={'text'}
            name='name'
            id='name'
            ref={register({ required: true })}
            placeholder='Titel'
          />
          {errors.name && <Form.ErrorMessage>Titel behövs</Form.ErrorMessage>}

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Form.Label htmlFor='name'>Beskriv avsnittet</Form.Label>
          <Controller
            control={control}
            name='description'
            render={(
              { onChange, onBlur, value, name, ref },
              { invalid, isTouched, isDirty }
            ) => (
              <Form.Editor
                content={currentSection.description}
                handleChange={(value) => onChange(value)}
                elementId={`edit-section-description-${currentSection.id}`}
                // placeholder='Beskrivning'
              />
            )}
          />

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Form.Label htmlFor='tags'>Lägg till taggar</Form.Label>
          <Controller
            control={control}
            name={'tags'}
            render={(
              { onChange, onBlur, value, name, ref },
              { invalid, isTouched, isDirty }
            ) => (
              <Form.Tags
                onChange={useCallback((tags) => {
                      onChange(tags)
                    }, [onChange])}
                tags={currentSection.tags}
                suggestions={tags}
              />
            )}
          />

          <Divider size={DIVIDER_SIZE.SMALL} />

          <ButtonWithLoader
            type={BUTTON_TYPE.PRIMARY}
            isLoading={isPosting}
            disabledText={'Uppdatera avsnitt'}
            enabledText={'Uppdatera avsnitt'}
            disabled={!isDirty || isPosting}
          />
        </Form>
      )}
    </>
  )
}

export default EditSectionPanel
